import React, { useState, useEffect, useRef } from 'react';
import { Chess, Piece } from 'chess.js';
import { TudorChessboard } from '../components/TudorChessboard';
import { Play, RotateCcw, Settings, Plus, Minus, Sword, Eye, Package, ChevronDown, RotateCw, Trophy, Shield, Activity, Lock } from 'lucide-react';
import { Inventory, Profile } from '../types';
import { SupabaseService } from '../services/supabaseService';

// Constants
const INITIAL_INVENTORY: Inventory = { p: 8, n: 2, b: 2, r: 2, q: 1, k: 1 };
const PIECE_VALUES: Record<string, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };
const PIECE_ICONS: Record<string, string> = { p: '♟︎', n: '♞', b: '♝', r: '♜', q: '♛', k: '♚' };

const ROW1_SLOTS = ['r', 'n', 'b', 'q', 'k', 'b', 'n', 'r'];

const STARTER_PACKS = [
    { name: 'Standard Legion', inventory: { p: 32, n: 4, b: 4, r: 6, q: 1, k: 1 } },
    { name: 'The Horde', inventory: { p: 64, n: 2, b: 2, r: 2, q: 1, k: 1 } },
    { name: 'Royal Guard', inventory: { p: 24, n: 6, b: 6, r: 6, q: 1, k: 1 } }
];

// --- WAR STRATEGY ENGINE ---
interface WarStrategy {
    abdicationThreshold: number;
    conscriptionBias: 'save' | 'spend';
    description: string;
}

const assessWarState = (myInv: Inventory, enemyInv: Inventory): WarStrategy => {
    const myQueens = myInv.q || 0;
    const enemyQueens = enemyInv.q || 0;
    const myPawns = myInv.p || 0;

    // 1. CRITICAL PRESERVATION: If we have no Queens (or last one) and enemy has them, run fast.
    if (myQueens <= 1 && enemyQueens >= 1) {
        return {
            abdicationThreshold: -400, // Abdicate if slightly losing to save the Queen
            conscriptionBias: 'save',
            description: 'Critical Preservation (Save the Queen)'
        };
    }

    // 2. ECONOMIC CONSERVATION: Low on pawns? Don't spend them.
    if (myPawns < 15) {
        return {
            abdicationThreshold: -1500, // Standard-ish
            conscriptionBias: 'save', // Hoard pawns
            description: 'Economic Conservation (Hoard Pawns)'
        };
    }

    // 3. AGGRESSION: We are rich and strong.
    if (myQueens > enemyQueens && myPawns > 30) {
        return {
            abdicationThreshold: -5000, // Fight to the death
            conscriptionBias: 'spend', // Buy elites
            description: 'Total War (Aggressive)'
        };
    }

    // Default
    return {
        abdicationThreshold: -2500,
        conscriptionBias: 'spend',
        description: 'Standard Engagement'
    };
};

const WarSimulation: React.FC = () => {
    // State
    const [whiteInv, setWhiteInv] = useState<Inventory>({ ...INITIAL_INVENTORY });
    const [blackInv, setBlackInv] = useState<Inventory>({ ...INITIAL_INVENTORY });

    const [userProfile, setUserProfile] = useState<Profile | null>(null);

    useEffect(() => {
        SupabaseService.getProfile().then(p => setUserProfile(p));
    }, []);

    useEffect(() => {
        if (userProfile && !userProfile.is_premium) {
            setSimCount(3);
        }
    }, [userProfile]);

    // Lifetime Purchase History for scaling costs
    const [whiteBoughtHistory, setWhiteBoughtHistory] = useState<Inventory>({ p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 });
    const [blackBoughtHistory, setBlackBoughtHistory] = useState<Inventory>({ p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 });

    // Lifetime Combat Stats (Class-based Kills/Deaths)
    const [whiteCombatStats, setWhiteCombatStats] = useState<Record<string, { kills: number, deaths: number }>>({
        p: { kills: 0, deaths: 0 }, n: { kills: 0, deaths: 0 }, b: { kills: 0, deaths: 0 }, r: { kills: 0, deaths: 0 }, q: { kills: 0, deaths: 0 }, k: { kills: 0, deaths: 0 }
    });
    const [blackCombatStats, setBlackCombatStats] = useState<Record<string, { kills: number, deaths: number }>>({
        p: { kills: 0, deaths: 0 }, n: { kills: 0, deaths: 0 }, b: { kills: 0, deaths: 0 }, r: { kills: 0, deaths: 0 }, q: { kills: 0, deaths: 0 }, k: { kills: 0, deaths: 0 }
    });

    // Lifetime Purchase History for scaling costs
    //const [whiteBoughtHistory, setWhiteBoughtHistory] = useState<Inventory>({ p:0, n:0, b:0, r:0, q:0, k:0 });
    //const [blackBoughtHistory, setBlackBoughtHistory] = useState<Inventory>({ p:0, n:0, b:0, r:0, q:0, k:0 });

    // Lifetime Combat Stats (Class-based Kills/Deaths)
    //const [whiteCombatStats, setWhiteCombatStats] = useState<Record<string, { kills: number, deaths: number }>>({
    //    p: { kills: 0, deaths: 0 }, n: { kills: 0, deaths: 0 }, b: { kills: 0, deaths: 0 }, r: { kills: 0, deaths: 0 }, q: { kills: 0, deaths: 0 }, k: { kills: 0, deaths: 0 }
    //});
    //const [blackCombatStats, setBlackCombatStats] = useState<Record<string, { kills: number, deaths: number }>>({
    //    p: { kills: 0, deaths: 0 }, n: { kills: 0, deaths: 0 }, b: { kills: 0, deaths: 0 }, r: { kills: 0, deaths: 0 }, q: { kills: 0, deaths: 0 }, k: { kills: 0, deaths: 0 }
    //});

    // Live Stats
    const [activeMaterial, setActiveMaterial] = useState<{ white: number, black: number, whitePieces: number, blackPieces: number }>({ white: 0, black: 0, whitePieces: 0, blackPieces: 0 });

    const [simCount, setSimCount] = useState(1);
    const [progress, setProgress] = useState(0);
    const [results, setResults] = useState({ white: 0, black: 0, draw: 0 });
    const [isSimulating, setIsSimulating] = useState(false);
    const [currentFen, setCurrentFen] = useState('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'); // Safe Start
    const [isWatchMode, setIsWatchMode] = useState(true);
    const [logs, setLogs] = useState<string[]>([]);
    // Using simple object/null is tricky with React state batching often, but fine for simple UI toggle
    const [presetMenuOpen, setPresetMenuOpen] = useState<{ side: 'white' | 'black', isOpen: boolean } | null>(null);

    const engineRef = useRef<Worker | null>(null);

    // Initialize Engine
    useEffect(() => {
        try {
            const worker = new Worker('/stockfish.js');
            worker.onmessage = (e) => {
                // Initial message handler (maybe readyok)
            };
            worker.postMessage('uci');
            engineRef.current = worker;
            return () => { worker.terminate(); };
        } catch (err) {
            console.error("Failed to load Stockfish", err);
            setLogs(l => [...l, "Error: Failed to load Stockfish engine."]);
        }
    }, []);

    // Helper: Calculate Max Strength (Initial Draft)
    const calculateStrength = (inv: Inventory) => Object.entries(inv).reduce((sum, [k, v]) => sum + (v || 0) * (PIECE_VALUES[k] || 0), 0);
    const initialWhiteStrength = calculateStrength(whiteInv);
    const initialBlackStrength = calculateStrength(blackInv);

    // Helper: Dynamic Piece Values based on War Performance
    const calculateDynamicPieceValues = (
        stats: Record<string, { kills: number, deaths: number }>,
        strategy: WarStrategy
    ) => {
        const baseValues: Record<string, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };
        const dynamic: Record<string, number> = {};

        Object.keys(baseValues).forEach(type => {
            let val = baseValues[type];

            // Performance Bonus: +0.2 for every kill (Class Average)
            // Simplified: Total Kills / 10? No, let's just use raw kills as a "Prestige" metric.
            // If Rooks have 20 kills, they are veteran units.
            // We cap the bonus to avoid absurdity.
            const kills = stats[type]?.kills || 0;
            const bonus = Math.min(val * 2, kills * 0.1); // Max 200% bonus

            val += bonus;

            // Strategy Multiplier
            // If Strategy is "Critical Preservation" (Save Queen), Queen value Skyrockets.
            if (strategy.description.includes('Critical') && type === 'q') {
                val *= 5; // MASSIVE value to prevent trading
            }
            // If "Economic Conservation" (Hoard Pawns), Pawns are worth more
            if (strategy.conscriptionBias === 'save' && type === 'p') {
                val *= 1.5;
            }

            dynamic[type] = val;
        });
        return dynamic;
    };

    // Draft Helper
    const updateInv = (side: 'white' | 'black', piece: keyof Inventory, delta: number) => {
        const setter = side === 'white' ? setWhiteInv : setBlackInv;
        setter(prev => {
            const currentVal = prev[piece] || 0;
            if (piece === 'k' && delta < 0 && currentVal <= 1) return prev; // Cannot reduce King below 1
            const newVal = Math.max(0, currentVal + delta);
            return { ...prev, [piece]: newVal };
        });
    };

    const loadPreset = (side: 'white' | 'black', packName: string) => {
        const pack = STARTER_PACKS.find(p => p.name === packName);
        if (pack) {
            const inv = { ...pack.inventory } as Inventory; // Ensure typing
            if (side === 'white') setWhiteInv(inv);
            else setBlackInv(inv);
        }
        setPresetMenuOpen(null);
    };

    // Helper: Add inventories
    const addInventories = (a: Inventory, b: Inventory): Inventory => {
        return {
            p: (a.p || 0) + (b.p || 0),
            n: (a.n || 0) + (b.n || 0),
            b: (a.b || 0) + (b.b || 0),
            r: (a.r || 0) + (b.r || 0),
            q: (a.q || 0) + (b.q || 0),
            k: (a.k || 0) + (b.k || 0),
        };
    };

    // Helper: Subtract inventories (a - b), floor at 0
    const subtractInventories = (a: Inventory, b: Inventory): Inventory => {
        return {
            p: Math.max(0, (a.p || 0) - (b.p || 0)),
            n: Math.max(0, (a.n || 0) - (b.n || 0)),
            b: Math.max(0, (a.b || 0) - (b.b || 0)),
            r: Math.max(0, (a.r || 0) - (b.r || 0)),
            q: Math.max(0, (a.q || 0) - (b.q || 0)),
            k: Math.max(0, (a.k || 0) - (b.k || 0)),
        };
    };

    // FEN Generator & Deployment Logic
    const generateFen = (wInv: Inventory, bInv: Inventory) => {
        // 8x8 Board Grid (null = empty)
        // Ranks 0-7 (0 = Rank 8 (Black), 7 = Rank 1 (White))
        const board: (string | null)[][] = Array(8).fill(null).map(() => Array(8).fill(null));

        const wDeployed: Inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
        const bDeployed: Inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };

        // Deployment Zones: Strictly Standard Ranks
        // White: Rank 1 (Row 7), Rank 2 (Row 6)
        // Black: Rank 8 (Row 0), Rank 7 (Row 1)

        // RE-IMPLEMENTING CLEAN DEPLOY FUNCTION (Strict Orthodox Caps + Conscription)
        // Helper to get piece value
        const getPieceValue = (type: string) => {
            const vals: Record<string, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 }; // King doesn't count for power balancing
            return vals[type.toLowerCase()] || 0;
        };

        const deployClean = (
            inv: Inventory,
            boughtHistory: Inventory,
            isBlack: boolean,
            backRow: number,
            frontRow: number,
            enemyStrength: number,
            bias: 'save' | 'spend'
        ) => {
            const pool = { ...inv }; // Local copy to track what is used
            const used = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
            const events: string[] = [];

            // --- CONSCRIPTION PHASE (Holistic) ---
            const BASE_VALUES: Record<string, number> = { n: 3, b: 3, r: 5, q: 9, k: 1 };

            // Temporary tracking for this round to update history later
            const roundBought = { n: 0, b: 0, r: 0, q: 0, k: 0 };
            const conscripted: Inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
            let spentPawns = 0;

            // Ideal Composition
            const IDEAL = { q: 1, r: 2, n: 2, b: 2 };
            // Priority Order
            const PRIORITY = ['q', 'r', 'n', 'b']; // Queen first, then Rooks, then Minors

            // Only conscript if bias is 'spend' OR if we are critically missing a Queen
            const needsQueen = pool.q === 0;
            const canSpend = bias === 'spend' || needsQueen;

            if (canSpend) {
                for (const type of PRIORITY) {
                    const targetCount = IDEAL[type as keyof typeof IDEAL];
                    let currentCount = pool[type as keyof Inventory];

                    while (currentCount < targetCount) {
                        // Calculate Dynamic Cost
                        // Cost = Base Value + Surcharge
                        // Surcharge (Non-Queen): +3 for every 2 bought previously (lifetime)
                        // Surcharge (Queen): +3 for every 1 bought previously (lifetime)
                        const lifetimeBought = (boughtHistory[type as keyof Inventory] || 0) + roundBought[type as keyof typeof roundBought];

                        let surcharge = 0;
                        if (type === 'q') {
                            surcharge = lifetimeBought * 3;
                        } else {
                            surcharge = Math.floor(lifetimeBought / 2) * 3;
                        }

                        const cost = BASE_VALUES[type] + surcharge;

                        // Ratio Check: Don't spend last 8 pawns (Front Line) unless it's a Queen
                        const minPawns = type === 'q' ? 0 : 8;

                        if (pool.p >= cost + minPawns) {
                            // BUY
                            pool.p -= cost;
                            spentPawns += cost;
                            pool[type as keyof Inventory]++;
                            currentCount++;

                            conscripted[type as keyof Inventory]++;
                            roundBought[type as keyof typeof roundBought]++;

                            events.push(`${isBlack ? 'Black' : 'White'} Buys ${type.toUpperCase()} (${cost}p)`);
                        } else {
                            break; // Cannot afford more of this type
                        }
                    }
                }
            }

            // --- DEPLOYMENT PHASE ---
            // Standard Back Rank Template
            const template = ['r', 'n', 'b', 'q', 'k', 'b', 'n', 'r'];

            for (let i = 0; i < 8; i++) {
                const type = template[i];
                if (pool[type as keyof Inventory] > 0) {
                    board[backRow][i] = isBlack ? type.toLowerCase() : type.toUpperCase();
                    pool[type as keyof Inventory]--;
                    used[type as keyof Inventory]++;
                }
            }

            // Front Rank (Pawns)
            for (let i = 0; i < 8; i++) {
                if (pool.p > 0) {
                    board[frontRow][i] = isBlack ? 'p' : 'P';
                    pool.p--;
                    used.p++;
                }
            }

            return { used, events, spentPawns, conscripted };
        };

        // Helper to Calc Strength
        const getInvStrength = (inv: Inventory) =>
            (inv.p || 0) * 1 + (inv.n || 0) * 3 + (inv.b || 0) * 3 + (inv.r || 0) * 5 + (inv.q || 0) * 9;

        const wStr = getInvStrength(wInv);
        const bStr = getInvStrength(bInv);

        // Deploy White (Rank 1=Row7, Rank 2=Row6). Pass BLACK Strength as target.
        const wStrategy = assessWarState(wInv, bInv);
        const { used: wUsed, events: wEvents, spentPawns: wSpent, conscripted: wConscripted } = deployClean(
            wInv, whiteBoughtHistory, false, 7, 6, bStr, wStrategy.conscriptionBias
        );
        Object.assign(wDeployed, wUsed);

        // Deploy Black (Rank 8=Row0, Rank 7=Row1). Pass WHITE Strength as target.
        const bStrategy = assessWarState(bInv, wInv);
        const { used: bUsed, events: bEvents, spentPawns: bSpent, conscripted: bConscripted } = deployClean(
            bInv, blackBoughtHistory, true, 0, 1, wStr, bStrategy.conscriptionBias
        );
        Object.assign(bDeployed, bUsed);

        // Return events too?
        // generateFen signature returns object. Let's add events.
        const allEvents = [...wEvents, ...bEvents];

        // Convert Board to FEN
        let fenRows: string[] = [];
        for (let r = 0; r < 8; r++) {
            let rowStr = "";
            let empty = 0;
            for (let c = 0; c < 8; c++) {
                const char = board[r][c];
                if (char) {
                    if (empty > 0) rowStr += empty;
                    empty = 0;
                    rowStr += char;
                } else {
                    empty++;
                }
            }
            if (empty > 0) rowStr += empty;
            fenRows.push(rowStr);
        }

        // Castling rights (simplified)
        let castling = "";
        if (board[7][4] === 'K') {
            if (board[7][7] === 'R') castling += "K";
            if (board[7][0] === 'R') castling += "Q";
        }
        // Black (Rank 0)
        if (board[0][4] === 'k') {
            if (board[0][7] === 'r') castling += "k";
            if (board[0][0] === 'r') castling += "q";
        }
        if (castling === "") castling = "-";

        return {
            fen: `${fenRows.join('/')} w ${castling} - 0 1`,
            whiteDeployed: wDeployed,
            blackDeployed: bDeployed,
            events: allEvents,
            whiteSpentPawns: wSpent,
            blackSpentPawns: bSpent,
            whiteConscripted: wConscripted,
            blackConscripted: bConscripted
        };
    };

    // Helper: Update Active Material Counts
    const updateMaterialStats = (game: Chess) => {
        let w = 0, b = 0, wP = 0, bP = 0;
        const board = game.board();
        for (let r = 0; r < 8; r++) {
            for (let c = 0; c < 8; c++) {
                const p = board[r][c];
                if (p) {
                    const val = PIECE_VALUES[p.type] || 0;
                    if (p.color === 'w') { w += val; wP++; }
                    else { b += val; bP++; }
                }
            }
        }
        setActiveMaterial({ white: w, black: b, whitePieces: wP, blackPieces: bP });
    };

    // Helper: Get Survivors as Inventory
    const getSurvivors = (game: Chess, color: 'w' | 'b'): Inventory => {
        const inv: Inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
        const board = game.board();
        for (let r = 0; r < 8; r++) {
            for (let c = 0; c < 8; c++) {
                const p = board[r][c];
                if (p && p.color === color) {
                    const key = p.type as keyof Inventory;
                    if (inv[key] !== undefined) inv[key]++;
                }
            }
        }
        // Ensure at least 0
        return inv;
    };


    // Update board preview when draft changes
    useEffect(() => {
        if (!isSimulating) {
            const { fen } = generateFen(whiteInv, blackInv);
            setCurrentFen(fen);

            // Also update material stats for preview
            try {
                const g = new Chess(fen);
                updateMaterialStats(g);
            } catch (e) { }
        }
    }, [whiteInv, blackInv, isSimulating]);


    // Simulation Loop
    const runSimulation = async () => {
        if (!engineRef.current) {
            alert("Engine not loaded or failed.");
            return;
        }

        setIsSimulating(true);
        setResults({ white: 0, black: 0, draw: 0 });
        setLogs([]);
        setProgress(0);

        // Local state for attrition
        let currentWhite = { ...whiteInv };
        let currentBlack = { ...blackInv };

        for (let i = 0; i < simCount; i++) {
            // Check for War Victory Conditions
            // 1. Decapitation (No Kings left)
            // 2. Attrition (Less than 8 Pawns = Cannot form a rank)
            if (currentWhite.k < 1 || currentBlack.k < 1) {
                setLogs(l => [...l, "Simulation Ended: King Eliminated."]);
                break;
            }
            if (currentWhite.p < 8) {
                setLogs(l => [...l, "Simulation Ended: White has insufficient manpower (< 8 Pawns). Black Wins the War."]);
                break;
            }
            if (currentBlack.p < 8) {
                setLogs(l => [...l, "Simulation Ended: Black has insufficient manpower (< 8 Pawns). White Wins the War."]);
                break;
            }

            // Snapshot inventories BEFORE the battle (for Mercy Rule restoration)
            const preBattleWhite = { ...currentWhite };
            const preBattleBlack = { ...currentBlack };

            // 1. GENERATE BATTLEFIELD & CALCULATE RESERVES (With Conscription)
            const { fen: startFen, whiteDeployed, blackDeployed, events, whiteSpentPawns, blackSpentPawns, whiteConscripted, blackConscripted } = generateFen(currentWhite, currentBlack);

            // UPDATE LIFETIME BOUGHT HISTORY
            setWhiteBoughtHistory(prev => addInventories(prev, whiteConscripted));
            setBlackBoughtHistory(prev => addInventories(prev, blackConscripted));

            // UPDATE LIFETIME BOUGHT HISTORY
            setWhiteBoughtHistory(prev => addInventories(prev, whiteConscripted));
            setBlackBoughtHistory(prev => addInventories(prev, blackConscripted));

            // Log Conscription Events
            if (events.length > 0) {
                setLogs(l => [...l, ...events]);
            }

            // WAR STRATEGY ASSESSMENT
            const whiteStrategy = assessWarState(currentWhite, currentBlack);
            const blackStrategy = assessWarState(currentBlack, currentWhite);

            // Log Strategies if changed/interesting
            if (i === 0 || whiteStrategy.description.includes('Critical') || blackStrategy.description.includes('Critical')) {
                setLogs(l => [...l, `[WAR STRATEGY] White: ${whiteStrategy.description} | Black: ${blackStrategy.description}`]);
            }

            // Calculate Undeployed (Safe in Barracks)
            const whiteUndeployed = subtractInventories(currentWhite, whiteDeployed);
            whiteUndeployed.p = Math.max(0, whiteUndeployed.p - whiteSpentPawns); // DEDUCT SPENT PAWNS!

            const blackUndeployed = subtractInventories(currentBlack, blackDeployed);
            blackUndeployed.p = Math.max(0, blackUndeployed.p - blackSpentPawns); // DEDUCT SPENT PAWNS!

            // Track Kills for Recruitment (Inventory format)
            const whiteKills: Inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
            const blackKills: Inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };

            let game = new Chess(startFen);
            let moves = 0;
            let abdicationCounter = 0; // Persistence check
            let abdication: 'white' | 'black' | null = null; // Track who resigned

            if (isWatchMode) {
                setCurrentFen(startFen);
                updateMaterialStats(game);
                // Pause briefly between battles
                await new Promise(r => setTimeout(r, 500));
            }

            while (!game.isGameOver() && moves < 250) {
                const fen = game.fen();

                // Track evaluation for this move
                let currentEval = 0;
                let isMate = false;

                const bestMove = await new Promise<string>((resolve) => {
                    const worker = engineRef.current!;
                    // Store candidates: { move: string, score: number, mate: boolean, depth: number }
                    const candidates: any[] = [];

                    const handler = (e: MessageEvent) => {
                        const msg = e.data;
                        if (typeof msg === 'string') {
                            // Parse MultiPV Lines
                            if (msg.startsWith('info') && msg.includes('score') && msg.includes('pv')) {
                                const parts = msg.split(' ');

                                // Extract Move (PV start)
                                const pvIdx = parts.indexOf('pv');
                                const move = parts[pvIdx + 1];

                                // Extract Score
                                const scoreIdx = parts.indexOf('score');
                                let score = 0;
                                let isM = false;
                                if (scoreIdx !== -1) {
                                    const type = parts[scoreIdx + 1];
                                    score = parseInt(parts[scoreIdx + 2]);
                                    if (type === 'mate') isM = true;
                                }

                                // Extract MultiPV Index
                                const mpvIdx = parts.indexOf('multipv');
                                const rank = mpvIdx !== -1 ? parseInt(parts[mpvIdx + 1]) : 1;

                                // Store/Update candidate
                                candidates[rank - 1] = { move, score, isM };

                                // Update Main Eval if rank 1
                                if (rank === 1) {
                                    currentEval = score;
                                    isMate = isM;
                                }
                            }

                            // Parse Best Move (End of Search)
                            if (msg.startsWith('bestmove')) {
                                worker.removeEventListener('message', handler);

                                // --- WAR WRAPPER: META-ENGINE EVALUATION ---
                                // 1. Calculate Dynamic Values for both sides
                                const wDyn = calculateDynamicPieceValues(whiteCombatStats, whiteStrategy);
                                const bDyn = calculateDynamicPieceValues(blackCombatStats, blackStrategy);

                                let selectedMove = msg.split(' ')[1];

                                // Only override if we have valid candidates to compare
                                if (candidates.length > 0) {
                                    const currentTurn = game.turn(); // 'w' or 'b'
                                    const myDyn = currentTurn === 'w' ? wDyn : bDyn;
                                    const enemyDyn = currentTurn === 'w' ? bDyn : wDyn;

                                    // Score each candidate move based on WAR OBJECTIVES
                                    const scoredCandidates = candidates.map(c => {
                                        // Start with Engine Score (Centipawns)
                                        let warScore = c.score;
                                        if (c.isM) warScore = c.score > 0 ? 10000 : -10000;

                                        // Simulate Move to detect Captures / Trades
                                        try {
                                            const simGame = new Chess(fen); // Clone current state
                                            const moveResult = simGame.move(c.move); // Execute candidate

                                            if (moveResult && moveResult.captured) {
                                                // CAPTURE DETECTED
                                                const victimType = moveResult.captured; // 'p', 'n', ...
                                                const victimValue = enemyDyn[victimType] || 0;

                                                const aggressorType = moveResult.piece;
                                                const aggressorValue = myDyn[aggressorType] || 0;

                                                // HEURISTIC: TRADE RISK
                                                // If we capture a piece, is it a "Free" capture or a "Trade"?
                                                // Engine Score usually tells us this (if score jumps, it's free. If score stays flat, it's a trade).
                                                // BUT, Engine assumes Standard Values (Rook=5).
                                                // We have Dynamic Values (Rook might be 8).

                                                // If Engine says "Equal Trade" (Score ~0), but we are losing a High Value piece for a Low Value one?
                                                // Example: My Veteran Rook (8) takes Enemy Rook (5).
                                                // Engine: +0.
                                                // War Logic: I lost 3 points of value.

                                                // Simplified Trade Check:
                                                // If the move places our piece on a square attacked by enemy? (Hard to know without engine 'attacked' data)
                                                // PROXY: If we capture a protected piece, we assume we lose our piece.
                                                // PROXY 2: We use the `warScore` adjustment directly.

                                                // Value Differential Adjustment
                                                // Gain: Victim Value (Dynamic)
                                                // Loss (Potential): Aggressor Value (Dynamic)

                                                // If Engine Score is neutral (-50 to +50), assume it is a Trade.
                                                // In a Trade, Net Change = VictimVal - AggressorVal.
                                                // Standard Engine calculated: VictimStd - AggressorStd.
                                                // Correction = (VictimDyn - AggressorDyn) - (VictimStd - AggressorStd)

                                                // Let's just apply a "Prestige Penalty" if risking a high value unit for a low one.
                                                if (aggressorValue > victimValue + 2) {
                                                    // Risking an Elite for a lesser piece.
                                                    // If score is not winning (>100), discourage this.
                                                    if (c.score < 100) {
                                                        warScore -= 200; // Penalize "Trading Down"
                                                    }
                                                }

                                                // "Bounty Bonus": If capturing a High Value enemy (e.g. Queen Slayer)
                                                if (victimValue > 10) {
                                                    warScore += 50; // Incentive to kill heroes
                                                }
                                            }
                                        } catch (e) { }

                                        return { ...c, warScore };
                                    });

                                    // Sort by new War Score
                                    scoredCandidates.sort((a, b) => b.warScore - a.warScore);

                                    // Log Override if top move changed
                                    if (scoredCandidates[0].move !== selectedMove && candidates[0].move === selectedMove) {
                                        // Only log occasionally to avoid spam
                                        if (Math.random() < 0.05) {
                                            // setLogs(l => [...l, `General Overruled: ${selectedMove} -> ${scoredCandidates[0].move}`]);
                                        }
                                    }

                                    selectedMove = scoredCandidates[0].move;
                                }

                                resolve(selectedMove);
                            }
                        }
                    };
                    worker.addEventListener('message', handler);
                    worker.postMessage(`position fen ${fen}`);
                    worker.postMessage('go depth 12 multipv 3');
                });

                const side = game.turn() === 'w' ? 'white' : 'black';
                const strategy = side === 'white' ? whiteStrategy : blackStrategy;

                // ADJUST THRESHOLDS (Fight Longer)
                // If "Critical Preservation", use -400. Else use -4000.
                const dynamicThreshold = strategy.abdicationThreshold <= -500 ? -4000 : -400;

                if ((!isMate && currentEval <= dynamicThreshold) || (isMate && currentEval < 0)) {
                    abdicationCounter++;

                    // PERSISTENCE CHECK: Must be failing for 4 consecutive checks (approx 2 full moves)
                    if (abdicationCounter >= 4) {
                        const isWhite = side === 'white';

                        // 1. Calculate Value of My Army on Board (What I would SAVE)
                        const mySurvivors = getSurvivors(game, isWhite ? 'w' : 'b');
                        const myTotalKings = isWhite ? currentWhite.k : currentBlack.k;
                        // King Value: Only value efficiently (1000) if Mate is imminent or position is Hopeless (-2500)
                        const isLastKing = myTotalKings <= 1;
                        const isCritical = isLastKing && (isMate || currentEval < -2500);
                        const decisionKingValue = isCritical ? 1000 : 4;
                        const displayKingValue = 4; // Standard King Value for "Material" display

                        // Helper for value calculation (Strategic & Economic)
                        const calculateStrategicValue = (inv: Inventory, kV: number, totalPawnsAvailable: number) => {
                            // Base Combat Values
                            const BASE_VALS = { p: 1, n: 3, b: 3, r: 5, q: 9, k: kV };

                            // Economic Surcharges (Replacement Cost)
                            // If I lose a piece, how expensive is it to replace?
                            // Surcharge approx: (Count / 2) * 3 or similar.
                            // Simplified: Value pieces higher if they are elites.

                            let totalVal = 0;

                            // Pawns (The Currency)
                            totalVal += (inv.p || 0) * 1;

                            // Knights/Bishops (Cost ~6 + Surcharge)
                            // Standard Value 3. Real Cost 6+. Ratio ~2x.
                            totalVal += (inv.n || 0) * (3 + 1); // Slight premium
                            totalVal += (inv.b || 0) * (3 + 1);

                            // Rooks (Cost 8+. Value 5. Ratio ~1.6x)
                            totalVal += (inv.r || 0) * (5 + 1.5);

                            // Queens (Cost 12+. Value 9. Ratio ~1.3x)
                            // BUT Queens have Inflation: +3 per Queen owned.
                            // We estimate avg surcharge for current army size.
                            const qCount = inv.q || 0;
                            const qSurcharge = Math.max(0, (qCount - 1) * 3);
                            totalVal += qCount * (9 + 2 + (qSurcharge / (qCount || 1))); // Baked in premium

                            // Kings
                            totalVal += (inv.k || 0) * kV;

                            // Scarcity Multiplier
                            // If we are poor (low pawns), existing pieces are IRREPLACEABLE (Infinite Value).
                            // If we have < 15 pawns, boost value to encourage saving them.
                            if (totalPawnsAvailable < 15) {
                                totalVal *= 1.25;
                            }

                            return totalVal;
                        };

                        const myPoolP = isWhite ? currentWhite.p : currentBlack.p;
                        const myDecisionValue = calculateStrategicValue(mySurvivors, decisionKingValue, myPoolP);
                        const myDisplayValue = calculateStrategicValue(mySurvivors, displayKingValue, myPoolP); // For log consistency

                        // WAR PENALTY (Strategic Cost of Losing)
                        // It costs morale/territory to lose. Only abdicate if Saving > Penalty.
                        // Base Penalty: 1000 (roughly a King's worth).
                        const WAR_PENALTY = 1000;

                        // 3. Strategic Decision
                        // Only abdicate if we save significantly more than the cost of losing.
                        if (myDecisionValue > WAR_PENALTY) {
                            // ABDICATE
                            abdication = side;
                            const saveMsg = isLastKing ? `${myDisplayValue} (King Critical)` : `${myDisplayValue}`;
                            setLogs(l => [...l, `Battle ${i + 1}: ${side === 'white' ? 'White' : 'Black'} Abdicates (Eval: ${currentEval}) - Saving: ${saveMsg} > Penalty: ${WAR_PENALTY}`]);
                            break;
                        } else {
                            // FIGHT TO THE END
                            // setLogs(l => [...l, `Refusing Abdication: Saving ${myDecisionValue} < Penalty`]);
                        }
                    }
                } else {
                    abdicationCounter = 0; // Reset if situation improves
                }

                if (bestMove && bestMove !== '(none)') {
                    try {
                        const moveResult = game.move(bestMove); // Execute Move

                        // UPDATE STATS (Kills/Deaths)
                        if (moveResult) {
                            if (moveResult.captured) {
                                // Aggressor gets a kill
                                const aggressorColor = moveResult.color; // 'w' or 'b'
                                const aggressorType = moveResult.piece; // 'p', 'n'...
                                const victimType = moveResult.captured;

                                const victimKey = victimType as keyof Inventory;
                                if (aggressorColor === 'w') {
                                    setWhiteCombatStats(prev => ({
                                        ...prev,
                                        [aggressorType]: { ...prev[aggressorType], kills: prev[aggressorType].kills + 1 }
                                    }));
                                    setBlackCombatStats(prev => ({
                                        ...prev,
                                        [victimType]: { ...prev[victimType], deaths: prev[victimType].deaths + 1 }
                                    }));
                                    // Track Capture for White Recruitment
                                    if (whiteKills[victimKey] !== undefined) whiteKills[victimKey]++;
                                } else {
                                    setBlackCombatStats(prev => ({
                                        ...prev,
                                        [aggressorType]: { ...prev[aggressorType], kills: prev[aggressorType].kills + 1 }
                                    }));
                                    setWhiteCombatStats(prev => ({
                                        ...prev,
                                        [victimType]: { ...prev[victimType], deaths: prev[victimType].deaths + 1 }
                                    }));
                                    // Track Capture for Black Recruitment
                                    if (blackKills[victimKey] !== undefined) blackKills[victimKey]++;
                                }
                            }
                        }

                        moves++;
                        if (isWatchMode) {
                            setCurrentFen(game.fen());
                            updateMaterialStats(game);
                            await new Promise(r => setTimeout(r, 20));
                        }
                    } catch (e) {
                        console.error("Move error:", bestMove);
                        break;
                    }
                } else {
                    break;
                }
            }

            // Record Result & Calculate Survivors
            let survivorsBoardWhite = getSurvivors(game, 'w');
            let survivorsBoardBlack = getSurvivors(game, 'b');

            // PRISONER RECRUITMENT LOGIC (Post-Battle)
            // Determine Winner
            let winnerSide: 'white' | 'black' | 'draw' = 'draw';
            if (abdication) {
                winnerSide = abdication === 'white' ? 'black' : 'white';
            } else if (game.isCheckmate()) {
                winnerSide = game.turn() === 'w' ? 'black' : 'white';
            }

            // Apply Attrition first (Base State)
            if (abdication) {
                if (winnerSide === 'white') {
                    // White (Winner) gets Pre-Battle Inventory MINUS Spent Pawns PLUS Conscripted Units
                    const restored = { ...preBattleWhite };
                    restored.p = Math.max(0, restored.p - whiteSpentPawns);
                    currentWhite = addInventories(restored, whiteConscripted);
                    // Black (Loser) gets Survivors + Undeployed
                    currentBlack = addInventories(survivorsBoardBlack, blackUndeployed);
                } else {
                    // Black (Winner) gets Pre-Battle Inventory MINUS Spent Pawns PLUS Conscripted Units
                    const restored = { ...preBattleBlack };
                    restored.p = Math.max(0, restored.p - blackSpentPawns);
                    currentBlack = addInventories(restored, blackConscripted);
                    // White (Loser) gets Survivors + Undeployed
                    currentWhite = addInventories(survivorsBoardWhite, whiteUndeployed);
                }
            } else {
                currentWhite = addInventories(survivorsBoardWhite, whiteUndeployed);
                currentBlack = addInventories(survivorsBoardBlack, blackUndeployed);
            }

            // Update Score
            if (winnerSide !== 'draw') {
                setResults(prev => ({ ...prev, [winnerSide]: prev[winnerSide as keyof typeof prev] + 1 }));
            } else {
                setResults(prev => ({ ...prev, draw: prev.draw + 1 }));
            }

            // --- PRISONER RECRUITMENT (Both Sides) ---
            const recruitEvents: string[] = [];
            const RECRUIT_CHANCE: Record<string, number> = { p: 0.5, n: 0.2, b: 0.2, r: 0.1, q: 0.05, k: 0 };

            // Helper for Rolling Recruits
            const rollRecruits = (kills: Inventory, sideName: string): Inventory => {
                const recruits: Inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
                (Object.keys(kills) as Array<keyof Inventory>).forEach(type => {
                    const count = kills[type];
                    for (let c = 0; c < count; c++) {
                        if (Math.random() < RECRUIT_CHANCE[type]) {
                            recruits[type]++;
                            recruitEvents.push(`${sideName} captured and recruited a ${type.toUpperCase()}`);
                        }
                    }
                });
                return recruits;
            };

            const whiteRecruits = rollRecruits(whiteKills, 'White');
            const blackRecruits = rollRecruits(blackKills, 'Black');

            if (recruitEvents.length > 0) {
                setLogs(l => [...l, ...recruitEvents]);
            }

            // Add recruits to final inventories
            currentWhite = addInventories(currentWhite, whiteRecruits);
            currentBlack = addInventories(currentBlack, blackRecruits);

            // Update UI State
            setWhiteInv(currentWhite);
            setBlackInv(currentBlack);

            setProgress(i + 1);
            if (!isWatchMode) await new Promise(r => setTimeout(r, 0));
        }

        setIsSimulating(false);
    };

    const DraftColumn = ({ side, inv }: { side: 'white' | 'black', inv: Inventory }) => (
        <div className={`
            p-6 rounded-lg border-2 w-full transition-all duration-300 relative
            ${side === 'white'
                ? 'bg-zinc-900 border-white/20 shadow-[0_0_20px_rgba(255,255,255,0.05)]'
                : 'bg-black border-zinc-800 shadow-[0_0_20px_rgba(0,0,0,0.5)]'
            }
        `}>
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h3 className={`text-xl font-serif font-bold tracking-widest uppercase flex items-center gap-2 ${side === 'white' ? 'text-white' : 'text-zinc-500'}`}>
                        <Shield size={18} /> {side} Army
                    </h3>
                    <div className="h-0.5 w-12 bg-tudor-gold mt-2"></div>
                </div>

                {/* Preset Menu */}
                <div className="relative">
                    <button
                        onClick={() => setPresetMenuOpen(prev => (prev?.isOpen && prev.side === side) ? null : { side, isOpen: true })}
                        className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-tudor-gold hover:text-white transition-colors border border-tudor-gold/30 px-2 py-1"
                    >
                        <Package size={12} /> Presets <ChevronDown size={12} />
                    </button>

                    {presetMenuOpen?.isOpen && presetMenuOpen.side === side && (
                        <div className="absolute right-0 top-8 z-50 w-48 bg-tudor-black border border-tudor-gold/30 rounded shadow-xl py-2">
                            {STARTER_PACKS.map(pack => (
                                <button
                                    key={pack.name}
                                    onClick={() => loadPreset(side, pack.name)}
                                    className="w-full text-left px-4 py-2 hover:bg-tudor-gold/10 text-[10px] text-gray-300 hover:text-white transition-colors uppercase tracking-wider block"
                                >
                                    {pack.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
                {Object.entries(PIECE_ICONS).map(([key, icon]) => (
                    <div key={key} className="flex flex-col items-center justify-between group hover:bg-white/5 p-2 rounded transition-colors border border-zinc-800/50">
                        <span className="text-2xl drop-shadow-md mb-2">{icon}</span>

                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => updateInv(side, key as keyof Inventory, -1)}
                                className="w-5 h-5 flex items-center justify-center bg-zinc-800 hover:bg-red-900/30 border border-zinc-700 hover:border-red-500/50 rounded text-gray-400 hover:text-red-400 transition-all text-xs"
                            >
                                <Minus size={10} />
                            </button>
                            <span className={`w-6 text-center font-mono font-bold text-sm ${inv[key as keyof Inventory] > 0 ? 'text-white' : 'text-zinc-700'}`}>
                                {inv[key as keyof Inventory] || 0}
                            </span>
                            <button
                                onClick={() => updateInv(side, key as keyof Inventory, 1)}
                                className="w-5 h-5 flex items-center justify-center bg-zinc-800 hover:bg-green-900/30 border border-zinc-700 hover:border-green-500/50 rounded text-gray-400 hover:text-green-400 transition-all text-xs"
                            >
                                <Plus size={10} />
                            </button>
                        </div>
                        <span className="text-[9px] uppercase text-zinc-600 mt-1 tracking-wider">{key}</span>
                    </div>
                ))}
            </div>

            <div className="mt-4 pt-4 border-t border-zinc-800/50 flex justify-between items-center text-sm">
                <span className="text-zinc-500 uppercase tracking-widest text-[10px]">Total Strength</span>
                <span className="text-tudor-gold font-mono font-bold text-lg drop-shadow-[0_0_10px_rgba(197,160,89,0.3)]">
                    {Object.entries(inv).reduce((sum, [k, v]) => sum + (v || 0) * (PIECE_VALUES[k] || 0), 0)}
                </span>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-tudor-black pt-12 pb-12 px-4 md:px-8 text-white font-sans overflow-x-hidden">

            {/* HEADER ROW */}
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center mb-8 border-b border-zinc-800 pb-4">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-gradient-to-b from-red-900/20 to-transparent border border-red-500/30 rounded-full shadow-[0_0_15px_rgba(220,38,38,0.2)]">
                        <Sword className="text-red-500 w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-serif text-tudor-gold uppercase tracking-[0.2em] drop-shadow-md">War Enactment</h1>
                        <p className="text-gray-500 text-[10px] tracking-widest uppercase">Tactical Simulation Engine</p>
                    </div>
                </div>

                <div className="flex items-center gap-4 mt-4 md:mt-0">
                    {/* Controls Mini-Panel */}
                    <div className="flex items-center gap-2 bg-zinc-900 p-2 rounded border border-zinc-800">
                        <div className="flex bg-black rounded p-1 border border-zinc-800 mr-2 items-center gap-2 px-3">
                            {!userProfile?.is_premium ? (
                                <div className="flex items-center gap-2">
                                    <Lock size={12} className="text-zinc-600" />
                                    <span className="text-[10px] text-zinc-600 uppercase tracking-wider">Sim Locked: 3x</span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-zinc-500 uppercase">Sim Count:</span>
                                    <input
                                        type="range"
                                        min="1"
                                        max="100"
                                        value={simCount}
                                        onChange={(e) => setSimCount(parseInt(e.target.value))}
                                        className="w-24 h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-tudor-gold"
                                    />
                                    <span className="text-[10px] text-tudor-gold font-mono w-6 text-right">{simCount}x</span>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => setIsWatchMode(!isWatchMode)}
                            className={`p-2 rounded border transition-all ${isWatchMode ? 'bg-tudor-gold/10 border-tudor-gold text-tudor-gold' : 'bg-black border-zinc-800 text-gray-500'}`}
                            title="Toggle Watch Mode"
                        >
                            <Eye size={18} />
                        </button>

                        <button
                            onClick={runSimulation}
                            disabled={isSimulating}
                            className={`
                                px-6 py-2 uppercase font-bold tracking-wider text-xs transition-all rounded flex items-center gap-2
                                ${isSimulating
                                    ? 'bg-zinc-800 text-zinc-500 cursor-wait border border-zinc-700'
                                    : 'bg-red-900 hover:bg-red-800 text-white border border-red-500 shadow-[0_0_15px_rgba(220,38,38,0.4)]'
                                }
                            `}
                        >
                            {isSimulating ? <RotateCw size={14} className="animate-spin" /> : <Play size={14} fill="currentColor" />}
                            {isSimulating ? `ENGAGED` : `SIMULATE`}
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto">

                {/* TOP AREA: BOARD & LIVE STATS */}
                <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr_300px] gap-8 mb-12 items-start">

                    {/* LEFT STATS (White) */}
                    <div className="hidden lg:flex flex-col gap-4 sticky top-12">
                        <div className="bg-zinc-900/50 border border-tudor-gold/10 p-4 rounded text-right">
                            <h3 className="text-white font-serif uppercase tracking-widest text-sm mb-1">White Force</h3>
                            <div className="text-4xl font-mono font-bold text-white drop-shadow-[0_0_5px_rgba(255,255,255,0.5)] transition-all">
                                {isSimulating ? activeMaterial.white : initialWhiteStrength}
                            </div>
                            <div className="text-[10px] text-zinc-500 uppercase mt-1">Material Strength</div>

                            {/* Reserve Bar */}
                            <div className="w-full h-1 bg-zinc-800 mt-2 relative overflow-hidden rounded-full">
                                <div
                                    className="absolute right-0 top-0 bottom-0 bg-white transition-all duration-300"
                                    style={{ width: `${Math.min(100, ((isSimulating ? activeMaterial.white : initialWhiteStrength) / Math.max(1, initialWhiteStrength)) * 100)}%` }}
                                ></div>
                            </div>
                            <div className="text-[10px] text-zinc-500 mt-1 flex justify-end gap-1">
                                <Activity size={12} /> {isSimulating ? activeMaterial.whitePieces : Object.values(whiteInv).reduce((a, b) => a + b, 0)} Units
                            </div>
                        </div>

                        {/* Result Box (White) */}
                        <div className="bg-zinc-900/50 border border-tudor-gold/10 p-4 rounded text-right">
                            <div className="text-tudor-gold text-2xl font-bold">{results.white}</div>
                            <div className="text-[10px] text-zinc-600 uppercase">Victories</div>
                        </div>
                    </div>

                    {/* CENTER BOARD */}
                    <div className="flex flex-col gap-4">
                        <div className="relative w-full aspect-square max-w-[650px] mx-auto border-[1px] border-zinc-700 bg-black shadow-2xl rounded-sm">
                            {/* Overlay Stats for Mobile */}
                            <div className="lg:hidden absolute top-2 left-2 z-10 bg-black/50 backdrop-blur rounded p-2 border border-white/20">
                                <div className="text-white text-xs font-bold">{activeMaterial.white}</div>
                            </div>
                            <div className="lg:hidden absolute top-2 right-2 z-10 bg-black/50 backdrop-blur rounded p-2 border border-white/20">
                                <div className="text-zinc-400 text-xs font-bold">{activeMaterial.black}</div>
                            </div>

                            <TudorChessboard position={currentFen} arePiecesDraggable={false} />

                            {/* Status Overlay */}
                            <div className="absolute -bottom-6 w-full text-center">
                                {isSimulating ? (
                                    <div className="text-tudor-gold text-xs uppercase tracking-[0.3em] animate-pulse">
                                        Simulation in Progress · {(progress / simCount * 100).toFixed(0)}%
                                    </div>
                                ) : (
                                    <div className="text-zinc-600 text-[10px] uppercase tracking-[0.2em]">
                                        Standing By
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Progress Bar */}
                        {isSimulating && (
                            <div className="mt-4 h-1 bg-black rounded-full overflow-hidden border border-zinc-800 max-w-[650px] mx-auto">
                                <div
                                    className="h-full bg-tudor-gold transition-all duration-300 shadow-[0_0_10px_rgba(197,160,89,0.5)]"
                                    style={{ width: `${(progress / simCount) * 100}%` }}
                                ></div>
                            </div>
                        )}
                        {/* Note: Battle Log Removed from here */}
                    </div>

                    {/* RIGHT STATS (Black) */}
                    <div className="hidden lg:flex flex-col gap-4 sticky top-12 h-fit">
                        <div className="bg-zinc-900/50 border border-zinc-700 p-4 rounded text-left">
                            <h3 className="text-zinc-500 font-serif uppercase tracking-widest text-sm mb-1">Black Force</h3>
                            <div className="text-4xl font-mono font-bold text-zinc-400 drop-shadow-[0_0_5px_rgba(0,0,0,0.5)] transition-all">
                                {isSimulating ? activeMaterial.black : initialBlackStrength}
                            </div>
                            <div className="text-[10px] text-zinc-600 uppercase mt-1">Material Strength</div>

                            {/* Reserve Bar */}
                            <div className="w-full h-1 bg-zinc-800 mt-2 relative overflow-hidden rounded-full">
                                <div
                                    className="absolute left-0 top-0 bottom-0 bg-zinc-500 transition-all duration-300"
                                    style={{ width: `${Math.min(100, ((isSimulating ? activeMaterial.black : initialBlackStrength) / Math.max(1, initialBlackStrength)) * 100)}%` }}
                                ></div>
                            </div>
                            <div className="text-[10px] text-zinc-600 mt-1 flex justify-start gap-1">
                                <Activity size={12} /> {isSimulating ? activeMaterial.blackPieces : Object.values(blackInv).reduce((a, b) => a + b, 0)} Units
                            </div>
                        </div>

                        {/* Result Box (Black) */}
                        <div className="bg-zinc-900/50 border border-zinc-800 p-4 rounded text-left">
                            <div className="text-zinc-400 text-2xl font-bold">{results.black}</div>
                            <div className="text-[10px] text-zinc-600 uppercase">Victories</div>
                        </div>

                        {/* Draws */}
                        <div className="bg-zinc-900/30 border border-zinc-800 p-4 rounded text-center opacity-50">
                            <div className="text-zinc-500 text-xl font-bold">{results.draw}</div>
                            <div className="text-[10px] text-zinc-700 uppercase">Stalemates</div>
                        </div>

                        {/* BATTLE LOG (Moved Here) */}
                        <div className="mt-4 border border-zinc-800 rounded bg-black/50 p-2 flex flex-col h-[250px]">
                            <h4 className="text-[10px] uppercase text-zinc-500 mb-2 tracking-widest font-bold border-b border-zinc-900 pb-1">Battle Log</h4>
                            <div className="flex-1 overflow-y-auto scrollbar-hide flex flex-col-reverse space-y-1">
                                {logs.length === 0 && <span className="text-zinc-800 italic text-xs p-2">Waiting to Engage...</span>}
                                {logs.map((log, i) => (
                                    <div key={i} className="text-zinc-400 border-b border-zinc-900/50 pb-0.5 last:border-0 text-xs font-mono break-words leading-tight">
                                        <span className="text-tudor-gold mr-1 text-[10px]">{'>'}</span>{log}
                                    </div>
                                )).reverse()}
                            </div>
                        </div>
                    </div>

                </div>

                {/* BOTTOM AREA: DRAFTING */}
                <div className="border-t border-zinc-800 pt-8 animate-fade-in-up">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-lg font-serif text-zinc-400 uppercase tracking-widest">Initial Deployment Config</h2>
                        <div className="text-[10px] text-zinc-600 uppercase">Modify Army Composition</div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <DraftColumn side="white" inv={whiteInv} />
                        <DraftColumn side="black" inv={blackInv} />
                    </div>
                </div>

            </div>
        </div>
    );
};

export default WarSimulation;
